import Vue from 'vue';
import {
    MemberAccountResponseModel,
    AccountStatementsResponseModel,
    AccountStatementDetail,
  } from '@/@types/member';
import GenerateXLSService from '@/services/GenerateXLS.service';
import { has, assign, sortBy } from 'lodash';
import moment from 'moment';


interface Data {
    xlsDataOverview: any[];
    xlsDataDetails: any[];
}

const STATUS_POSTED = 'Posted';
const STATUS_NEEDS_RECEIPT = 'Needs Receipt';

export default Vue.extend({
    data(): Data {
        return {
            xlsDataOverview: [],
            xlsDataDetails: [],
        };
      },
    computed: {
        memberAccountStatements(): AccountStatementsResponseModel {
            return this.$store.getters.memberAccountStatements ? this.$store.getters.memberAccountStatements : {};
          },
        account(): MemberAccountResponseModel {
            return this.$store.getters.currentAccount(this.$route.params.id);
        },
        isHSA(): boolean {
            return this.account.productType === 'HSA';
        },
    },
    methods: {
        createXls() {
            this.xlsDataOverview = this.getStatementOverview();
            this.xlsDataDetails = this.getStatementDetails();
            GenerateXLSService.exportXLS(
                [
                    {
                        sheetName: `${this.$options.filters.varReplace(this.$options.filters!.t('xls-statement-overview-sheet-name'), {accountDescription: this.account.description})}`,
                        sheet: this.xlsDataOverview,
                        skipHeader: true,
                    },
                    {
                        sheetName: this.$options.filters!.t('xls-statement-details-sheet-name'),
                        sheet: this.xlsDataDetails,
                        customHeaders: this.getStatementDetailHeaders(),
                        skipHeader: false,
                    },
                ],
                null,
                'statement',
             );
          },

        getStatementOverview(): any[] {
            const statementOverview = [];
            if (this.memberAccountStatements.startDate) {
                statementOverview.push({
                    'label': this.$options.filters!.t('xls-statement-overview-label-start-date'),
                    'value': this.$options.filters!.moment(this.memberAccountStatements.startDate, 'MM-DD-YY'),
                });
            }
            if (this.memberAccountStatements.endDate) {
                statementOverview.push({
                    'label': this.$options.filters!.t('xls-statement-overview-label-end-date'),
                    'value': this.$options.filters!.moment(this.memberAccountStatements.endDate, 'MM-DD-YY'),

                });
            }
            if (this.memberAccountStatements.planType) {
                statementOverview.push({
                    'label': this.$options.filters!.t('xls-statement-overview-label-plan-type'),
                    'value': this.memberAccountStatements.planType,
                });
            }
            if (this.memberAccountStatements.termDate) {
                statementOverview.push({
                    'label': this.$options.filters!.t('xls-statement-overview-label-termination-date'),
                    'value': this.$options.filters!.moment(this.memberAccountStatements.termDate, 'MM-DD-YY'),
                });
            }
            if (this.memberAccountStatements.graceEndDate) {
                statementOverview.push({
                    'label': this.$options.filters!.t('xls-statement-overview-label-grace-period-end-date'),
                    'value': this.$options.filters!.moment(this.memberAccountStatements.graceEndDate, 'MM-DD-YY'),
                });
            }
            if (this.memberAccountStatements.claimFilingDeadline) {
                statementOverview.push({
                    'label': this.$options.filters!.t('xls-statement-overview-label-claims-filing-deadline'),
                    'value': this.memberAccountStatements.runoutPeriod ?
                        `${this.memberAccountStatements.runoutPeriod} ${this.$options.filters!.t('xls-statement-overview-value-months-from-claim')}`
                        : this.$options.filters!.moment(this.memberAccountStatements.claimFilingDeadline, 'MM-DD-YY'),
                });
            }
            if (this.memberAccountStatements.threshold) {
                statementOverview.push({
                    'label': this.$options.filters!.t('xls-statement-overview-label-threshold'),
                    'value': this.$options.filters.currency(this.memberAccountStatements.threshold),
                });
            }
            if (this.memberAccountStatements.accumulation) {
                statementOverview.push({
                    'label': this.$options.filters!.t('xls-statement-overview-label-accumulation-amount'),
                    'value': this.$options.filters.currency(this.memberAccountStatements.accumulation.amount),
                });
            }
            if (this.memberAccountStatements.beginingBalance) {
                statementOverview.push({
                    'label': `${this.$options.filters.varReplace(this.$options.filters!.t('xls-statement-overview-label-beginning-balance'), {startDate: this.memberAccountStatements.reportStartDate ? this.$options.filters!.moment(this.memberAccountStatements.reportStartDate, 'MM-DD-YY') : ''})}`,
                    'value': this.$options.filters.currency(this.memberAccountStatements.beginingBalance),
                });
            }
            if (this.memberAccountStatements.rollover) {
                statementOverview.push({
                    'label': this.$options.filters!.t('xls-statement-overview-label-rollover-from-prior-year'),
                    'value': this.$options.filters.currency(this.memberAccountStatements.rollover),
                });
            }
            if (this.memberAccountStatements.currentTaxYearContributions) {
                statementOverview.push({
                    'label': this.$options.filters!.t('xls-statement-overview-label-contributions-current'),
                    'value': this.$options.filters.currency(this.memberAccountStatements.currentTaxYearContributions.amount),
                });
            }
            if (this.memberAccountStatements.priorTaxYearContributions) {
                statementOverview.push({
                    'label': this.$options.filters!.t('xls-statement-overview-label-contributions-prior'),
                    'value': this.$options.filters.currency(this.memberAccountStatements.priorTaxYearContributions.amount),
                });
            }
            if (this.memberAccountStatements.election) {
                statementOverview.push({
                    'label': this.$options.filters!.t('xls-statement-overview-label-election-amount'),
                    'value': this.$options.filters.currency(this.memberAccountStatements.election),
                });
            }
            if (this.memberAccountStatements.employerFunding) {
                statementOverview.push({
                    'label': this.$options.filters!.t('xls-statement-overview-label-employer-funding'),
                    'value': this.$options.filters.currency(this.memberAccountStatements.employerFunding),
                });
            }
            if (this.memberAccountStatements.contributions) {
                statementOverview.push({
                    'label': this.$options.filters!.t('xls-statement-overview-label-contributions'),
                    'value': this.$options.filters.currency(this.memberAccountStatements.contributions.amount),
                });
            }
            if (this.memberAccountStatements.otherTransfers) {
                statementOverview.push({
                    'label': this.$options.filters!.t('xls-statement-overview-label-other-transfers'),
                    'value': this.$options.filters.currency(this.memberAccountStatements.otherTransfers.amount),
                });
            }
            if (this.memberAccountStatements.investmentTransfer) {
                statementOverview.push({
                    'label': this.$options.filters!.t('xls-statement-overview-label-investment-transfers'),
                    'value': this.$options.filters.currency(this.memberAccountStatements.investmentTransfer.amount),
                });
            }
            if (this.memberAccountStatements.interest) {
                statementOverview.push({
                    'label': this.$options.filters!.t('xls-statement-overview-label-interest-earned'),
                    'value': this.$options.filters.currency(this.memberAccountStatements.interest.amount),
                });
            }
            if (this.memberAccountStatements.claims) {
                statementOverview.push({
                    'label': this.$options.filters!.t('xls-statement-overview-label-withdrawals'),
                    'value': this.$options.filters.currency(this.memberAccountStatements.claims.amount),
                });
            }
            if (this.memberAccountStatements.fees) {
                statementOverview.push({
                    'label': this.$options.filters!.t('xls-statement-overview-label-fees'),
                    'value': this.$options.filters.currency(this.memberAccountStatements.fees.amount),
                });
            }
            if (this.memberAccountStatements.availableBalance) {
                statementOverview.push({
                    'label': this.$options.filters!.t('xls-statement-overview-label-available-balance'),
                    'value': this.$options.filters.currency(this.memberAccountStatements.availableBalance),
                });
            }
            return  statementOverview;
        },



        getStatementDetails(): any[] {
            const statementDetails = this.getMergedStatementDetails();
            if (this.isHSA) {
                return statementDetails.map( (detail) => {
                    return {
                        postedDate: detail.postedDate ? this.$options.filters!.moment(detail.postedDate, 'MM-DD-YY') : '',
                        taxYear: detail.taxYear,
                        category: detail.category,
                        description: detail.transactions,
                        amount: this.$options.filters.transactionFormat(this.$options.filters.currency(detail.amount)),
                    };
                });
            } else {
                return statementDetails.map( (detail) => {
                    return {
                        postedDate: detail.postedDate ? this.$options.filters!.moment(detail.postedDate, 'MM-DD-YY') : '',
                        category: detail.category,
                        description: detail.transactions,
                        amount: this.$options.filters.transactionFormat(this.$options.filters.currency(detail.amount)),
                    };
                });
            }
        },
        getStatementDetailHeaders(): object {
            if (this.isHSA) {
                return {
                        postedDate: this.$options.filters!.t('xls-statement-details-header-posted-date'),
                        taxYear: this.$options.filters!.t('xls-statement-details-header-tax-year'),
                        category: this.$options.filters!.t('xls-statement-details-header-category'),
                        description: this.$options.filters!.t('xls-statement-details-header-description'),
                        amount: this.$options.filters!.t('xls-statement-details-header-amount'),
                    };
            } else {
                return {
                    postedDate: this.$options.filters!.t('xls-statement-details-header-posted-date'),
                    category: this.$options.filters!.t('xls-statement-details-header-category'),
                    description: this.$options.filters!.t('xls-statement-details-header-description'),
                    amount: this.$options.filters!.t('xls-statement-details-header-amount'),
                };
            }
        },

        getMergedStatementDetails(): any[] {
            let statementDetails = [];
            if (this.memberAccountStatements.accumulation && has(this.memberAccountStatements.accumulation, 'details')) {
                statementDetails = statementDetails.concat(
                    this.memberAccountStatements.accumulation.details.map((detail) => assign(detail, {category: this.$options.filters!.t('xls-statement-details-category-accumulation')})),
                );
            }
            if (this.memberAccountStatements.contributions && has(this.memberAccountStatements.contributions, 'details')) {
                statementDetails = statementDetails.concat(
                    this.memberAccountStatements.contributions.details.map((detail) => assign(detail, {category: this.$options.filters!.t('xls-statement-details-category-contribution')})),
                );
            }
            if (this.memberAccountStatements.currentTaxYearContributions && has(this.memberAccountStatements.currentTaxYearContributions, 'details')) {
                statementDetails = statementDetails.concat(
                    this.memberAccountStatements.currentTaxYearContributions.details.map((detail) => assign(detail, {category: this.$options.filters!.t('xls-statement-details-category-contribution')})),
                );
            }
            if (this.memberAccountStatements.priorTaxYearContributions && has(this.memberAccountStatements.priorTaxYearContributions, 'details')) {
                statementDetails = statementDetails.concat(
                    this.memberAccountStatements.priorTaxYearContributions.details.map((detail) => assign(detail, {category: this.$options.filters!.t('xls-statement-details-category-contribution')})),
                );
            }
            if (this.memberAccountStatements.otherTransfers && has(this.memberAccountStatements.otherTransfers, 'details')) {
                statementDetails = statementDetails.concat(
                    this.memberAccountStatements.otherTransfers.details.map((detail) => assign(detail, {category: this.$options.filters!.t('xls-statement-details-category-transfer')})),
                );
            }
            if (this.memberAccountStatements.investmentTransfer && has(this.memberAccountStatements.investmentTransfer, 'details')) {
                statementDetails = statementDetails.concat(
                    this.memberAccountStatements.investmentTransfer.details.map((detail) => assign(detail, {category: this.$options.filters!.t('xls-statement-details-category-investment-transfer')})),
                );
            }
            if (this.memberAccountStatements.interest && has(this.memberAccountStatements.interest, 'details')) {
                statementDetails = statementDetails.concat(
                    this.memberAccountStatements.interest.details.map((detail) => assign(detail, {category: this.$options.filters!.t('xls-statement-details-category-interest')})),
                );
            }
            if (this.memberAccountStatements.claims && has(this.memberAccountStatements.claims, 'details')) {
                statementDetails = statementDetails.concat(
                    this.memberAccountStatements.claims.details.map((detail) => assign(detail, {category: this.$options.filters!.t('xls-statement-details-category-claim')})),
                );
            }
            if (this.memberAccountStatements.fees && has(this.memberAccountStatements.fees, 'details')) {
                statementDetails = statementDetails.concat(
                    this.memberAccountStatements.fees.details.map((detail) => assign(detail, {category: this.$options.filters!.t('xls-statement-details-category-fee')})),
                );
            }
            statementDetails = this.sortStatementDetails(statementDetails);
            return statementDetails;
          },
        sortStatementDetails (statementDetails): any[] {
            let sortedStatementDetails = [];
            statementDetails.map( (detail) => assign(detail, {postedTimeStamp: moment(detail.postedDate).valueOf()}));
            sortedStatementDetails = sortBy(statementDetails, ['postedTimeStamp', 'taxYear', 'amount']);
            return sortedStatementDetails;
        },
    },
});

